/**
 * EXP-2.1 / EXP-2.2 / EXP-2.4 · The words on the authoring side.
 *
 * ⚠️ THE ADVICE IS THE ONLY PLACE THE PRODUCT SHAPES WHAT GETS AUTHORED.
 * Everything else in this epic moves photos around. This paragraph is what
 * decides whether the examples are worth looking at, and an admin reads it
 * exactly once, on an empty editor, before deciding what to upload. It is
 * asserted on in `exampleAuthoringCopy.test.ts` so that a tidy-up of "just
 * wording" fails a test rather than quietly turning specific instructions into
 * encouragement. `mergedFilters.test.ts` is the precedent.
 */

/** The switch that sits with Required and Allow photo uploads. */
export const SWITCH_LABEL = 'Example photos';
export const SWITCH_DESCRIPTION = 'A chip on the field opens them';

/** The row the switch reveals, which leads into the examples editor. */
export function leadRowTitle(count: number): string {
  if (count === 0) return 'No examples yet';
  return `${count} example ${count === 1 ? 'photo' : 'photos'}`;
}

export function leadRowSubtitle(count: number): string {
  return count === 0
    ? 'The chip stays hidden until there is one'
    : 'Shown behind a chip on the field';
}

/** The button into the second screen. It says what it will do, not where. */
export function manageButtonLabel(count: number): string {
  return count === 0 ? 'Add example photos' : 'Manage examples';
}

/** The second screen's own header. */
export const EDITOR_TITLE = 'Example photos';

export function editorSubtitle(count: number): string {
  return count === 0 ? 'None yet' : `${count} on this item`;
}

/**
 * ⚠️ The back control NAMES THE ITEM it returns to, never the word "Back".
 * That is what tells someone mid-task they have not lost the item they were
 * editing — the panel slid, it did not navigate away.
 */
export function backLabel(itemName: string): string {
  const trimmed = itemName?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : 'the item';
}

/**
 * ⚠️ There is no Save on the second screen, and the footer says why.
 * A second Save there would imply examples commit on their own, which they do
 * not: captions, verdicts and order are written by Save item.
 */
export const EDITOR_FOOTER_NOTE = 'Saved when you save the item';
export const ADD_PHOTOS_BUTTON = 'Add photos';

/** The drop zone. */
export const DROP_ZONE_TITLE = 'Drop photos here, or browse';

/**
 * ⚠️ THIS MUST MATCH THE BUCKET. `checklist-examples` is capped at 10485760
 * bytes and restricted to PNG and JPEG by
 * `20260910c9_exp_1_2_checklist_examples_bucket.sql`. A drop zone promising
 * more than the bucket accepts is a failure the user sees and the developer
 * does not.
 */
export const DROP_ZONE_LIMITS = 'PNG or JPG, up to 10 MB each';
export const MAX_EXAMPLE_BYTES = 10 * 1024 * 1024;
export const ACCEPTED_EXAMPLE_TYPES = ['image/png', 'image/jpeg', 'image/jpg'] as const;

/**
 * EXP-2.4 · the advice. Specific, short, and actionable.
 *
 * It names a number, says what each shot is for, and ends on the thing that
 * makes an example useful rather than decorative: the sentence under it.
 */
export const AUTHORING_ADVICE =
  'Six is plenty. Two that show the shot done right, two of the mistakes that ' +
  'get a survey sent back, and one wide shot for context. Each one needs a ' +
  'sentence a tech can act on.';

/** The editor pane. */
export const CAPTION_LABEL = 'Why this one';
export const CAPTION_PLACEHOLDER = 'One sentence a tech can act on';
export const VERDICT_GOOD = 'Do this';
export const VERDICT_BAD = 'Not this';
export const MOVE_UP = 'Move up';
export const MOVE_DOWN = 'Move down';
export const REMOVE = 'Remove';

/**
 * ⚠️ Remove asks nothing, so the copy has to carry the warning instead.
 * EXP-2.3 is explicit that a confirmation dialog is the wrong answer here:
 * an admin removing a photo they just uploaded should not be interrogated, and
 * one they did not just upload is recoverable by re-uploading it.
 */
export const REMOVE_HINT = 'Removing is immediate. Re-upload the photo to undo it.';
